import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Gift, Download } from "lucide-react";
import { BuyButton } from "./BuyButton";

const EASE = [0.22, 1, 0.36, 1];

export const ExcerptReader = ({ content }) => {
  const pages = content.excerpt_pages || [];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  if (!pages.length) return null;
  const page = pages[index];

  const go = (dir) => {
    setDirection(dir);
    setIndex((i) => (i + dir + pages.length) % pages.length);
  };

  return (
    <section id="estratto" className="spotlight bg-white py-16 sm:py-24" data-testid="excerpt-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-12 max-w-2xl"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-olive-light px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-olive">
            <Gift className="h-3.5 w-3.5" /> Regalo per te
          </span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-olive sm:text-3xl lg:text-4xl" data-testid="excerpt-title">
            Sfoglia le pagine vere del quaderno
          </h2>
          <p className="mt-3 text-base text-stone-600 sm:text-lg">
            Niente rendering: queste sono fotografie delle pagine reali, le stesse che il tuo bambino colorerà e riempirà.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="relative overflow-hidden rounded-3xl border border-sand bg-cream p-6 shadow-md sm:p-10"
          data-testid="excerpt-reader"
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 60 * direction }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 * direction }}
              transition={{ duration: 0.35, ease: EASE }}
              className="grid items-center gap-8 md:grid-cols-2"
            >
              <div className="relative mx-auto w-full max-w-md">
                <span className="absolute -top-3 left-10 z-10 h-7 w-24 -rotate-6 rounded-sm bg-terracotta-light/90 shadow-sm" aria-hidden />
                <span className="absolute -bottom-3 right-10 z-10 h-7 w-24 rotate-3 rounded-sm bg-olive-light/90 shadow-sm" aria-hidden />
                <motion.div
                  whileHover={{ rotate: 0, scale: 1.02 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="-rotate-1 rounded-lg border-[10px] border-white bg-white shadow-2xl"
                >
                  <img src={page.image_url} alt={page.title} className="max-h-[420px] w-full rounded-sm object-contain" data-testid="excerpt-page-image" />
                </motion.div>
              </div>
              <div className="space-y-4">
                <span className="font-editorial text-sm italic tracking-wide text-terracotta" data-testid="excerpt-page-counter">
                  Pagina {index + 1} di {pages.length}
                </span>
                <h3 className="font-display text-xl font-semibold text-olive sm:text-2xl" data-testid="excerpt-page-title">
                  {page.title}
                </h3>
                <p className="text-base leading-relaxed text-stone-600 sm:text-lg" data-testid="excerpt-page-text">
                  {page.text}
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <motion.button
                    type="button"
                    onClick={() => go(-1)}
                    whileTap={{ scale: 0.9 }}
                    data-testid="prev-excerpt-page-button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-sand bg-white text-olive transition-colors hover:bg-warm"
                    aria-label="Pagina precedente"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => go(1)}
                    whileTap={{ scale: 0.9 }}
                    data-testid="next-excerpt-page-button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-terracotta text-white transition-colors hover:bg-terracotta-dark"
                    aria-label="Pagina successiva"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </motion.button>
                  <div className="ml-2 flex gap-1.5" data-testid="excerpt-page-dots">
                    {pages.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
                        data-testid={`excerpt-dot-${i}`}
                        className={`h-2.5 rounded-full transition-all ${i === index ? "w-6 bg-terracotta" : "w-2.5 bg-stone-300 hover:bg-stone-400"}`}
                        aria-label={`Vai alla pagina ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-center">
          <p className="w-full text-sm font-medium text-stone-500">Ti è piaciuto l'assaggio? Nel quaderno completo trovi tutte le attività.</p>
          <BuyButton url={content.amazon_url} price={content.price} testid="excerpt-buy-amazon-button" />
          <motion.a
            href="/files/scheda-omaggio-termometro-emozioni.pdf"
            download="Io-Emozione-Scheda-Omaggio.pdf"
            data-testid="download-free-pdf-button"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 rounded-full border-2 border-terracotta bg-white px-6 py-3 font-display text-base font-semibold text-terracotta transition-colors hover:bg-terracotta hover:text-white"
          >
            <Download className="h-5 w-5" /> Scarica la scheda omaggio (PDF)
          </motion.a>
        </div>
      </div>
    </section>
  );
};
